import React, { forwardRef } from "react";
import clsx from "clsx";
import _ from "lodash";
import { Col, Row, Dropdown, Form } from "react-bootstrap";

import { RequiredMark } from "components";
import { generateAutotestValue } from "helpers";
import { useLoader } from "hooks";

import Skeleton from "../skeleton";

import styles from "./dropdown.module.scss";

const CustomDropdown = (
	{
		label = "",
		selected = false,
		onSelect,
		disabled = false,
		height = "false",
		name = "",
		labelClassName = "",
		smLabel = 6,
		smSelect = 6,
		className = "",
		items = [],
		keyProp = "value",
		labelProp = "name",
		placeholder = "Select item",
		styleBoarding = false,
		blurLoading = false,
		autoTest = "",
		showRequiredMark = false,
		validateError = undefined,
		variant = "primary", // primary | secondary
		flip = true,
	},
	ref,
) => {
	const disabledClass = disabled ? styles.disabled : "";
	const widthColumns = label ? smSelect : 12;
	const selectedItem = _.find(items, item => item[keyProp] === selected) || {
		[keyProp]: null,
		[labelProp]: placeholder,
		isPlaceholder: true,
	};

	const selectedItemClass = selectedItem.isPlaceholder
		? ""
		: styles.selectedItem;

	const labelElement = label ? (
		<Form.Label
			column
			sm={smLabel}
			className={clsx(disabledClass, styles.formGroup, labelClassName)}
		>
			{label}
			{showRequiredMark && <RequiredMark />}
		</Form.Label>
	) : null;

	const isErrorStyle = validateError ? styles.dropdownError : null;
	const checkHeight = height === "true" ? styles.dropdownHeight : null;
	const checkStyleBoarding = styleBoarding ? styles.boardingStyle : null;
	const dataAutotest = `dropdown${generateAutotestValue(autoTest, name)}`;

	const Loader = useLoader(blurLoading);

	const dropdownClassName =
		variant === "secondary"
			? styles.dropdownSecondary
			: styles.dropdownBlock;

	return (
		<Form.Group
			as={Row}
			className={clsx(dropdownClassName, checkStyleBoarding, className)}
		>
			{labelElement}
			<Col sm={widthColumns}>
				<Loader
					skeleton={<Skeleton height="34px" />}
					component={
						<Dropdown data-autotest={dataAutotest}>
							<Dropdown.Toggle
								disabled={disabled}
								className={clsx(
									styles.dropdown,
									checkHeight,
									isErrorStyle,
								)}
							>
								<div
									className={clsx(
										styles.dropdownSelected,
										selectedItemClass,
									)}
								>
									{selectedItem[labelProp]}
								</div>
							</Dropdown.Toggle>
							<Dropdown.Menu
								className={styles.dropdownMenu}
								flip={flip}
							>
								{items?.map((item, key) => {
									const activeClass =
										item[keyProp] === selected
											? styles.active
											: "";
									return (
										<Row key={key}>
											<Col>
												<Dropdown.Item
													onSelect={() =>
														onSelect?.(
															name,
															item[keyProp],
														)
													}
													className={clsx(
														styles.dropdownItem,
														activeClass,
													)}
													disabled={disabled}
												>
													{item[labelProp]}
												</Dropdown.Item>
											</Col>
										</Row>
									);
								})}
							</Dropdown.Menu>
						</Dropdown>
					}
				/>
			</Col>
		</Form.Group>
	);
};

export default forwardRef(CustomDropdown);
